import React, { useState, useEffect } from 'react';
import { db } from '../../../utils/firebase';
import { collection, getDocs, doc, setDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import {
  Save, Loader, Eye, EyeOff, Plus, Trash2, ChevronUp, ChevronDown,
  Image, Type, LayoutGrid, PanelTop, ExternalLink, GripVertical
} from 'lucide-react';
import ImageUploader from '../common/ImageUploader';
import {
  CampaignPage, CampaignBlock, CampaignHeroBlock,
  CampaignProductGridBlock, CampaignImageBannerBlock, CampaignTextBlock, CampaignEditorialBlock, Product
} from '../../../types';

const generateId = () => `block_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const DEFAULT_HERO_BLOCK: () => CampaignHeroBlock = () => ({
  id: generateId(),
  type: 'hero',
  backgroundImage: '',
  overlayOpacity: 30,
  title: '',
  description: '',
  ctaButtons: [{ text: 'SHOP NOW', link: '/shop' }],
});

const DEFAULT_PRODUCT_GRID_BLOCK: () => CampaignProductGridBlock = () => ({
  id: generateId(),
  type: 'product_grid',
  title: '',
  productIds: [],
});

const DEFAULT_IMAGE_BANNER_BLOCK: () => CampaignImageBannerBlock = () => ({
  id: generateId(),
  type: 'image_banner',
  image: '',
  link: '',
});

const DEFAULT_TEXT_BLOCK: () => CampaignTextBlock = () => ({
  id: generateId(),
  type: 'text',
  heading: '',
  body: '',
  alignment: 'center',
});

const DEFAULT_EDITORIAL_BLOCK: () => CampaignEditorialBlock = () => ({
  id: generateId(),
  type: 'editorial',
  image: '',
  alt: '',
});

// ─── Product Picker ───────────────────────────────────────────
const ProductPicker: React.FC<{
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}> = ({ selectedIds, onChange }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, 'products'));
        const prods: Product[] = [];
        snap.forEach(d => prods.push({ id: d.id, ...d.data() } as Product));
        setProducts(prods);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filtered = products.filter(p =>
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleProduct = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(sid => sid !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="border border-gray-200 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products..."
          className="flex-1 text-xs p-2 border border-gray-200"
        />
        <span className="text-xs text-gray-400">{selectedIds.length} selected</span>
      </div>

      {/* Selected products */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedIds.map(id => {
            const p = products.find(pr => pr.id === id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 bg-black text-white text-[10px] px-2 py-1 font-heading uppercase tracking-wider cursor-pointer hover:bg-gray-700"
                onClick={() => toggleProduct(id)}
              >
                {p?.title || id}
                <Trash2 size={10} />
              </span>
            );
          })}
        </div>
      )}

      {/* Product list */}
      <div className="max-h-48 overflow-y-auto space-y-1">
        {loading ? (
          <div className="flex justify-center py-4"><Loader size={16} className="animate-spin" /></div>
        ) : (
          filtered.slice(0, 50).map(p => (
            <label
              key={p.id}
              className={`flex items-center gap-2 p-1.5 cursor-pointer hover:bg-gray-50 text-xs ${
                selectedIds.includes(p.id) ? 'bg-gray-100 font-bold' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(p.id)}
                onChange={() => toggleProduct(p.id)}
                className="accent-black"
              />
              {p.image && (
                <img src={p.image} alt="" className="w-8 h-10 object-cover bg-gray-100" />
              )}
              <span className="flex-1 truncate">{p.title}</span>
              <span className="text-gray-400">{p.brand}</span>
              <span className="text-gray-500">£{p.price?.toFixed(2)}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
};

// ─── Block Editor ─────────────────────────────────────────────
const BlockEditor: React.FC<{
  block: CampaignBlock;
  onUpdate: (block: CampaignBlock) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}> = ({ block, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const blockTypeLabel = {
    hero: 'Hero Section',
    product_grid: 'Product Grid',
    image_banner: 'Image Banner',
    text: 'Text Section',
    editorial: 'Editorial Image',
  }[block.type];

  const blockTypeIcon = {
    hero: <PanelTop size={14} />,
    product_grid: <LayoutGrid size={14} />,
    image_banner: <Image size={14} />,
    text: <Type size={14} />,
    editorial: <Image size={14} />,
  }[block.type];

  return (
    <div className="border border-gray-200 bg-white">
      {/* Block Header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <GripVertical size={14} className="text-gray-300" />
        <span className="text-gray-500">{blockTypeIcon}</span>
        <span className="font-heading font-bold text-xs uppercase tracking-widest flex-1">
          {blockTypeLabel}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={onMoveUp} disabled={isFirst} className="p-1 text-gray-400 hover:text-black disabled:opacity-20">
            <ChevronUp size={14} />
          </button>
          <button onClick={onMoveDown} disabled={isLast} className="p-1 text-gray-400 hover:text-black disabled:opacity-20">
            <ChevronDown size={14} />
          </button>
          <button onClick={onDelete} className="p-1 text-red-400 hover:text-red-600 ml-2">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Block Content */}
      <div className="p-4 space-y-4">
        {/* ── Hero Block ── */}
        {block.type === 'hero' && (
          <>
            {/* Background Image */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Background Image</label>
              <ImageUploader
                value={block.backgroundImage}
                onChange={(url) => onUpdate({ ...block, backgroundImage: url })}
                pathPrefix="content/campaign"
                aspectClass="aspect-video"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Title</label>
              <input
                type="text"
                value={block.title}
                onChange={(e) => onUpdate({ ...block, title: e.target.value })}
                className="w-full text-sm p-2 border border-gray-200 font-heading uppercase tracking-widest"
                placeholder="CAMPAIGN TITLE"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Description</label>
              <textarea
                value={block.description}
                onChange={(e) => onUpdate({ ...block, description: e.target.value })}
                className="w-full text-sm p-2 border border-gray-200"
                rows={3}
                placeholder="Campaign description text..."
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
                Overlay Opacity: {block.overlayOpacity}%
              </label>
              <input
                type="range"
                min="0"
                max="80"
                value={block.overlayOpacity}
                onChange={(e) => onUpdate({ ...block, overlayOpacity: parseInt(e.target.value) })}
                className="w-full"
              />
            </div>

            {/* CTA Buttons */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">CTA Buttons</label>
              {block.ctaButtons.map((btn, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={btn.text}
                    onChange={(e) => {
                      const updated = [...block.ctaButtons];
                      updated[i] = { ...updated[i], text: e.target.value };
                      onUpdate({ ...block, ctaButtons: updated });
                    }}
                    className="flex-1 text-xs p-2 border border-gray-200"
                    placeholder="SHOP MEN"
                  />
                  <input
                    type="text"
                    value={btn.link}
                    onChange={(e) => {
                      const updated = [...block.ctaButtons];
                      updated[i] = { ...updated[i], link: e.target.value };
                      onUpdate({ ...block, ctaButtons: updated });
                    }}
                    className="flex-1 text-xs p-2 border border-gray-200 font-mono"
                    placeholder="/shop/mens"
                  />
                  <button
                    onClick={() => {
                      const updated = block.ctaButtons.filter((_, idx) => idx !== i);
                      onUpdate({ ...block, ctaButtons: updated });
                    }}
                    className="p-2 text-red-400 hover:text-red-600"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => onUpdate({ ...block, ctaButtons: [...block.ctaButtons, { text: '', link: '' }] })}
                className="text-xs text-gray-500 hover:text-black flex items-center gap-1 mt-1"
              >
                <Plus size={12} /> Add Button
              </button>
            </div>
          </>
        )}

        {/* ── Product Grid Block ── */}
        {block.type === 'product_grid' && (
          <>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Section Title (Optional)</label>
              <input
                type="text"
                value={block.title || ''}
                onChange={(e) => onUpdate({ ...block, title: e.target.value })}
                className="w-full text-sm p-2 border border-gray-200"
                placeholder="SHOP THE COLLECTION"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Products</label>
              <ProductPicker
                selectedIds={block.productIds}
                onChange={(ids) => onUpdate({ ...block, productIds: ids })}
              />
            </div>
          </>
        )}

        {/* ── Image Banner Block ── */}
        {block.type === 'image_banner' && (
          <>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Image</label>
              <ImageUploader
                value={block.image}
                onChange={(url) => onUpdate({ ...block, image: url })}
                pathPrefix="content/campaign"
                aspectClass="aspect-video"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Link (Optional)</label>
              <input
                type="text"
                value={block.link || ''}
                onChange={(e) => onUpdate({ ...block, link: e.target.value })}
                className="w-full text-xs p-2 border border-gray-200 font-mono"
                placeholder="/shop/mens"
              />
            </div>
          </>
        )}

        {/* ── Text Block ── */}
        {block.type === 'text' && (
          <>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Heading (Optional)</label>
              <input
                type="text"
                value={block.heading || ''}
                onChange={(e) => onUpdate({ ...block, heading: e.target.value })}
                className="w-full text-sm p-2 border border-gray-200"
                placeholder="Section heading"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Body Text</label>
              <textarea
                value={block.body}
                onChange={(e) => onUpdate({ ...block, body: e.target.value })}
                className="w-full text-sm p-2 border border-gray-200"
                rows={4}
                placeholder="Body text content..."
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Alignment</label>
              <div className="flex gap-2">
                {(['left', 'center', 'right'] as const).map(a => (
                  <button
                    key={a}
                    onClick={() => onUpdate({ ...block, alignment: a })}
                    className={`px-4 py-1.5 text-xs font-heading uppercase tracking-widest border ${
                      block.alignment === a ? 'bg-black text-white border-black' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Background Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={block.backgroundColor || '#ffffff'}
                    onChange={(e) => onUpdate({ ...block, backgroundColor: e.target.value })}
                    className="w-8 h-8 border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={block.backgroundColor || ''}
                    onChange={(e) => onUpdate({ ...block, backgroundColor: e.target.value })}
                    className="flex-1 text-xs p-2 border border-gray-200 font-mono"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Text Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={block.textColor || '#000000'}
                    onChange={(e) => onUpdate({ ...block, textColor: e.target.value })}
                    className="w-8 h-8 border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={block.textColor || ''}
                    onChange={(e) => onUpdate({ ...block, textColor: e.target.value })}
                    className="flex-1 text-xs p-2 border border-gray-200 font-mono"
                    placeholder="#000000"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Editorial Block ── */}
        {block.type === 'editorial' && (
          <>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Image</label>
              <ImageUploader
                value={block.image}
                onChange={(url) => onUpdate({ ...block, image: url })}
                pathPrefix="content/campaign"
                aspectClass="aspect-video"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Alt Text (Optional)</label>
              <input
                type="text"
                value={block.alt || ''}
                onChange={(e) => onUpdate({ ...block, alt: e.target.value })}
                className="w-full text-xs p-2 border border-gray-200"
                placeholder="Describe the image..."
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Campaign Editor ──────────────────────────────────────────
const CampaignEditor: React.FC<{
  campaign: CampaignPage;
  onSave: (campaign: CampaignPage) => Promise<void>;
  onBack: () => void;
}> = ({ campaign: initial, onSave, onBack }) => {
  const [campaign, setCampaign] = useState<CampaignPage>(initial);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSave = async () => {
    if (!campaign.title.trim() || !campaign.slug.trim()) {
      setMessage({ type: 'error', text: 'Title and slug are required' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await onSave(campaign);
      setMessage({ type: 'success', text: 'Campaign saved successfully' });
    } catch (error) {
      console.error('Error saving campaign:', error);
      setMessage({ type: 'error', text: 'Failed to save campaign' });
    } finally {
      setSaving(false);
    }
  };

  const updateBlock = (index: number, updated: CampaignBlock) => {
    const blocks = [...campaign.blocks];
    blocks[index] = updated;
    setCampaign({ ...campaign, blocks });
  };

  const deleteBlock = (index: number) => {
    setCampaign({ ...campaign, blocks: campaign.blocks.filter((_, i) => i !== index) });
  };

  const moveBlock = (index: number, direction: -1 | 1) => {
    const blocks = [...campaign.blocks];
    const target = index + direction;
    if (target < 0 || target >= blocks.length) return;
    [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
    setCampaign({ ...campaign, blocks });
  };

  const addBlock = (type: CampaignBlock['type']) => {
    const block = {
      hero: DEFAULT_HERO_BLOCK,
      product_grid: DEFAULT_PRODUCT_GRID_BLOCK,
      image_banner: DEFAULT_IMAGE_BANNER_BLOCK,
      text: DEFAULT_TEXT_BLOCK,
      editorial: DEFAULT_EDITORIAL_BLOCK,
    }[type]();
    setCampaign({ ...campaign, blocks: [...campaign.blocks, block] });
  };

  const slugify = (text: string) =>
    text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-xs font-heading uppercase tracking-widest text-gray-500 hover:text-black"
          >
            &larr; All Campaigns
          </button>
          <h2 className="font-heading font-bold text-xl">
            {campaign.id ? 'Edit Campaign' : 'New Campaign'}
          </h2>
        </div>
        <div className="flex items-center gap-4">
          {campaign.slug && (
            <a
              href={`/campaign/${campaign.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-black"
            >
              <ExternalLink size={12} /> Preview
            </a>
          )}
          <button
            onClick={() => setCampaign({ ...campaign, visible: !campaign.visible })}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-heading font-bold uppercase tracking-widest border transition-colors ${
              campaign.visible
                ? 'bg-green-50 border-green-300 text-green-800 hover:bg-green-100'
                : 'bg-gray-50 border-gray-300 text-gray-500 hover:bg-gray-100'
            }`}
          >
            {campaign.visible ? <Eye size={14} /> : <EyeOff size={14} />}
            {campaign.visible ? 'Live' : 'Draft'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-black text-white px-6 py-2 font-heading font-bold uppercase tracking-widest hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2 text-sm"
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
        </div>
      </div>

      {message && (
        <div className={`p-3 mb-6 text-sm ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {/* Campaign Meta */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 p-4 bg-gray-50 border border-gray-200">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Campaign Title</label>
          <input
            type="text"
            value={campaign.title}
            onChange={(e) => {
              const title = e.target.value;
              const updates: Partial<CampaignPage> = { title };
              // Auto-generate slug for new campaigns
              if (!campaign.id) {
                updates.slug = slugify(title);
              }
              setCampaign({ ...campaign, ...updates });
            }}
            className="w-full text-sm p-2 border border-gray-200 font-heading"
            placeholder="Spring 2026 Collection"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">URL Slug</label>
          <div className="flex items-center">
            <span className="text-xs text-gray-400 font-mono mr-1">/campaign/</span>
            <input
              type="text"
              value={campaign.slug}
              onChange={(e) => setCampaign({ ...campaign, slug: slugify(e.target.value) })}
              className="flex-1 text-sm p-2 border border-gray-200 font-mono"
              placeholder="spring-2026"
            />
          </div>
        </div>
      </div>

      {/* Block List */}
      <div className="space-y-4 mb-6">
        {campaign.blocks.map((block, index) => (
          <BlockEditor
            key={block.id}
            block={block}
            onUpdate={(updated) => updateBlock(index, updated)}
            onDelete={() => deleteBlock(index)}
            onMoveUp={() => moveBlock(index, -1)}
            onMoveDown={() => moveBlock(index, 1)}
            isFirst={index === 0}
            isLast={index === campaign.blocks.length - 1}
          />
        ))}
      </div>

      {/* Add Block */}
      <div className="border-2 border-dashed border-gray-200 p-6">
        <p className="text-xs font-heading uppercase tracking-widest text-gray-400 text-center mb-4">Add Content Block</p>
        <div className="flex flex-wrap gap-3 justify-center">
          {[
            { type: 'hero' as const, label: 'Hero Section', icon: <PanelTop size={14} /> },
            { type: 'product_grid' as const, label: 'Product Grid', icon: <LayoutGrid size={14} /> },
            { type: 'image_banner' as const, label: 'Image Banner', icon: <Image size={14} /> },
            { type: 'text' as const, label: 'Text Section', icon: <Type size={14} /> },
            { type: 'editorial' as const, label: 'Editorial Image', icon: <Image size={14} /> },
          ].map(({ type, label, icon }) => (
            <button
              key={type}
              onClick={() => addBlock(type)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-xs font-heading uppercase tracking-widest hover:bg-black hover:text-white hover:border-black transition-colors"
            >
              {icon} {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Main Campaign Manager ────────────────────────────────────
const CampaignManager: React.FC = () => {
  const [campaigns, setCampaigns] = useState<CampaignPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CampaignPage | null>(null);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'campaigns'), orderBy('updatedAt', 'desc')));
      const list: CampaignPage[] = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() } as CampaignPage));
      setCampaigns(list);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (campaign: CampaignPage) => {
    const now = new Date().toISOString();
    const docId = campaign.id || campaign.slug;
    const data = {
      ...campaign,
      id: docId,
      updatedAt: now,
      createdAt: campaign.createdAt || now,
    };
    await setDoc(doc(db, 'campaigns', docId), data);
    setEditing(null);
    await fetchCampaigns();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this campaign page? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'campaigns', id));
      await fetchCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
    }
  };

  const createNew = () => {
    setEditing({
      id: '',
      slug: '',
      title: '',
      visible: false,
      blocks: [],
      createdAt: null,
      updatedAt: null,
    });
  };

  if (editing) {
    return (
      <CampaignEditor
        campaign={editing}
        onSave={handleSave}
        onBack={() => setEditing(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="py-8 flex justify-center">
        <Loader className="animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-heading font-bold text-xl">Campaign Pages</h2>
          <p className="text-sm text-gray-500 mt-1">
            Create dedicated landing pages for promotions and campaigns. Link them from the Promo Banner CTA.
          </p>
        </div>
        <button
          onClick={createNew}
          className="bg-black text-white px-6 py-2 font-heading font-bold uppercase tracking-widest hover:bg-gray-800 flex items-center gap-2 text-sm"
        >
          <Plus size={14} /> New Campaign
        </button>
      </div>

      {campaigns.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-gray-200">
          <p className="font-heading text-gray-400 uppercase tracking-widest text-sm mb-4">No campaigns yet</p>
          <button
            onClick={createNew}
            className="bg-black text-white px-6 py-2 font-heading font-bold uppercase tracking-widest hover:bg-gray-800 text-xs"
          >
            Create Your First Campaign
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <div
              key={c.id}
              className="flex items-center gap-4 p-4 border border-gray-200 hover:border-black transition-colors cursor-pointer group"
              onClick={() => setEditing(c)}
            >
              {/* Thumbnail from first hero block */}
              {(() => {
                const heroBlock = c.blocks.find(b => b.type === 'hero');
                const imgSrc = heroBlock && heroBlock.type === 'hero' ? heroBlock.backgroundImage : '';
                return (
                  <div className="w-20 h-14 bg-gray-100 flex-shrink-0 overflow-hidden">
                    {imgSrc ? (
                      <img src={imgSrc} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <Image size={20} />
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-bold text-sm uppercase tracking-wider truncate">
                    {c.title || 'Untitled Campaign'}
                  </h3>
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${
                    c.visible ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {c.visible ? 'Live' : 'Draft'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 font-mono mt-1">/campaign/{c.slug}</p>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span>{c.blocks.length} block{c.blocks.length !== 1 ? 's' : ''}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                  className="p-2 text-gray-300 hover:text-red-500 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignManager;
